import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import { obtenerPerfilProfesional, guardarPerfilProfesional } from "../../services/api";

// Brand colors - LemmonTags theme
const COLORS = {
  primary: "#84cc16",
  primaryDark: "#65a30d",
  primaryLight: "#ecfccb",
  background: "#fafafa",
  cardBg: "#ffffff",
  textDark: "#1f2937",
  textMuted: "#6b7280",
  border: "#e5e7eb",
  danger: "#dc2626",
  dangerLight: "#fef2f2",
  success: "#10b981",
  successLight: "#d1fae5"
};

export default function VetPerfilProfesional() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Form data
  const [formData, setFormData] = useState({
    nombreCompleto: "",
    cedulaProfesional: "",
    universidad: "",
    especialidad: "",
    nombreClinica: "",
    direccion: "",
    ciudad: "",
    telefono: "",
    email: ""
  });

  // Validation errors
  const [errores, setErrores] = useState({});

  // Track if profile was complete when loaded
  const [perfilCompletoOriginal, setPerfilCompletoOriginal] = useState(false);

  useEffect(() => {
    async function cargarPerfil() {
      try {
        const resp = await obtenerPerfilProfesional();
        if (resp.error) {
          setError(resp.error);
        } else if (resp.data) {
          const perfil = resp.data;
          setFormData({
            nombreCompleto: perfil.nombreCompleto || "",
            cedulaProfesional: perfil.cedulaProfesional || "",
            universidad: perfil.universidad || "",
            especialidad: perfil.especialidad || "",
            nombreClinica: perfil.nombreClinica || "",
            direccion: perfil.direccion || "",
            ciudad: perfil.ciudad || "",
            telefono: perfil.telefono || "",
            email: perfil.email || ""
          });
          setPerfilCompletoOriginal(perfil.perfilCompleto || false);
        }
      } catch (err) {
        console.error("Error cargando perfil:", err);
        setError("Error al cargar el perfil");
      } finally {
        setLoading(false);
      }
    }
    cargarPerfil();
  }, []);

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errores[name]) {
      setErrores(prev => ({ ...prev, [name]: "" }));
    }
  }

  function validarFormulario() {
    const nuevosErrores = {};

    if (!formData.nombreCompleto.trim()) {
      nuevosErrores.nombreCompleto = "El nombre completo es obligatorio";
    }
    if (!formData.cedulaProfesional.trim()) {
      nuevosErrores.cedulaProfesional = "La cedula profesional es obligatoria";
    }
    if (!formData.nombreClinica.trim()) {
      nuevosErrores.nombreClinica = "El nombre de la clinica es obligatorio";
    }

    setErrores(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!validarFormulario()) {
      toast.error("Por favor completa los campos obligatorios");
      return;
    }

    setSaving(true);
    try {
      const resp = await guardarPerfilProfesional(formData);
      if (resp.error) {
        toast.error(resp.error);
      } else {
        toast.success("Perfil actualizado");
        // If profile became complete, show special message
        if (!perfilCompletoOriginal && resp.data?.perfilCompleto) {
          toast.success("Tu perfil profesional esta completo. Ya puedes registrar visitas medicas.", {
            duration: 4000
          });
        }
        setPerfilCompletoOriginal(resp.data?.perfilCompleto || false);

        // Redirect to main panel after 1.5 seconds
        setTimeout(() => {
          navigate(`/vetPanel?token=${token}`);
        }, 1500);
      }
    } catch (err) {
      console.error("Error guardando perfil:", err);
      toast.error("Error al guardar el perfil");
    } finally {
      setSaving(false);
    }
  }

  function handleBack() {
    navigate(`/vetPanel?token=${token}`);
  }

  if (loading) {
    return (
      <div style={{
        minHeight: "100vh",
        backgroundColor: COLORS.background,
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}>
        <div className="text-center">
          <div className="spinner-border" style={{ color: COLORS.primary }} role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p style={{ color: COLORS.textMuted, marginTop: "12px" }}>Cargando perfil...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: "100vh",
        backgroundColor: COLORS.background,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px"
      }}>
        <div className="text-center" style={{ maxWidth: "400px" }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>⚠️</div>
          <h5 style={{ color: COLORS.textDark }}>Error al cargar perfil</h5>
          <p style={{ color: COLORS.textMuted }}>{error}</p>
          <button
            className="btn"
            style={{ backgroundColor: COLORS.primary, color: "white" }}
            onClick={handleBack}
          >
            Volver al panel
          </button>
        </div>
      </div>
    );
  }

  const perfilCompleto = formData.nombreCompleto.trim() &&
                         formData.cedulaProfesional.trim() &&
                         formData.nombreClinica.trim();

  return (
    <div style={{ minHeight: "100vh", backgroundColor: COLORS.background, padding: "20px" }}>
      <div style={{ maxWidth: "600px", margin: "0 auto" }}>
        {/* Header */}
        <div style={{ marginBottom: "24px" }}>
          <button
            className="btn btn-sm"
            style={{
              backgroundColor: "transparent",
              color: COLORS.textMuted,
              border: "none",
              padding: "8px 0",
              marginBottom: "12px"
            }}
            onClick={handleBack}
          >
            ← Volver al panel
          </button>
          <h2 style={{ color: COLORS.textDark, fontWeight: 600, marginBottom: "8px" }}>
            Mi Perfil Profesional
          </h2>
          <p style={{ color: COLORS.textMuted, margin: 0 }}>
            Completa tu perfil para poder registrar visitas medicas
          </p>
        </div>

        {/* Profile Status Banner */}
        {!perfilCompleto && (
          <div style={{
            backgroundColor: COLORS.dangerLight,
            border: `1px solid ${COLORS.danger}`,
            borderRadius: "8px",
            padding: "12px 16px",
            marginBottom: "20px",
            display: "flex",
            alignItems: "center",
            gap: "12px"
          }}>
            <span style={{ fontSize: "24px" }}>⚠️</span>
            <div>
              <p style={{ color: COLORS.danger, fontWeight: 600, margin: 0, fontSize: "14px" }}>
                Perfil incompleto
              </p>
              <p style={{ color: COLORS.textMuted, margin: 0, fontSize: "13px" }}>
                Completa los campos obligatorios para poder registrar visitas medicas
              </p>
            </div>
          </div>
        )}

        {perfilCompleto && (
          <div style={{
            backgroundColor: COLORS.successLight,
            border: `1px solid ${COLORS.success}`,
            borderRadius: "8px",
            padding: "12px 16px",
            marginBottom: "20px",
            display: "flex",
            alignItems: "center",
            gap: "12px"
          }}>
            <span style={{ fontSize: "24px" }}>✓</span>
            <div>
              <p style={{ color: COLORS.success, fontWeight: 600, margin: 0, fontSize: "14px" }}>
                Perfil completo
              </p>
              <p style={{ color: COLORS.textMuted, margin: 0, fontSize: "13px" }}>
                Puedes registrar visitas medicas con tus datos profesionales
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Professional Data Section */}
          <div className="card border-0 shadow-sm" style={{ backgroundColor: COLORS.cardBg, marginBottom: "20px" }}>
            <div className="card-header" style={{ backgroundColor: COLORS.primary, color: "white", border: "none" }}>
              <h6 className="mb-0" style={{ fontWeight: 600 }}>Datos Profesionales</h6>
            </div>
            <div className="card-body">
              {/* Nombre Completo */}
              <div className="mb-3">
                <label className="form-label" style={{ fontSize: "14px", fontWeight: 500, color: COLORS.textDark }}>
                  Nombre completo *
                </label>
                <input
                  type="text"
                  className={`form-control ${errores.nombreCompleto ? 'is-invalid' : ''}`}
                  name="nombreCompleto"
                  value={formData.nombreCompleto}
                  onChange={handleChange}
                  placeholder="Ej: Dr. Juan Garcia Lopez"
                  style={{ fontSize: "14px" }}
                />
                {errores.nombreCompleto && (
                  <div className="invalid-feedback">{errores.nombreCompleto}</div>
                )}
                <small style={{ color: COLORS.textMuted }}>
                  Este nombre aparecera en los registros medicos
                </small>
              </div>

              {/* Cedula Profesional */}
              <div className="mb-3">
                <label className="form-label" style={{ fontSize: "14px", fontWeight: 500, color: COLORS.textDark }}>
                  Cedula profesional *
                </label>
                <input
                  type="text"
                  className={`form-control ${errores.cedulaProfesional ? 'is-invalid' : ''}`}
                  name="cedulaProfesional"
                  value={formData.cedulaProfesional}
                  onChange={handleChange}
                  placeholder="Ej: 12345678"
                  style={{ fontSize: "14px" }}
                />
                {errores.cedulaProfesional && (
                  <div className="invalid-feedback">{errores.cedulaProfesional}</div>
                )}
              </div>

              {/* Universidad */}
              <div className="mb-3">
                <label className="form-label" style={{ fontSize: "14px", fontWeight: 500, color: COLORS.textDark }}>
                  Universidad (opcional)
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="universidad"
                  value={formData.universidad}
                  onChange={handleChange}
                  placeholder="Ej: UNAM"
                  style={{ fontSize: "14px" }}
                />
              </div>

              {/* Especialidad */}
              <div className="mb-0">
                <label className="form-label" style={{ fontSize: "14px", fontWeight: 500, color: COLORS.textDark }}>
                  Especialidad (opcional)
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="especialidad"
                  value={formData.especialidad}
                  onChange={handleChange}
                  placeholder="Ej: Pequenas especies"
                  style={{ fontSize: "14px" }}
                />
              </div>
            </div>
          </div>

          {/* Clinic Data Section */}
          <div className="card border-0 shadow-sm" style={{ backgroundColor: COLORS.cardBg, marginBottom: "20px" }}>
            <div className="card-header" style={{ backgroundColor: COLORS.primaryDark, color: "white", border: "none" }}>
              <h6 className="mb-0" style={{ fontWeight: 600 }}>Datos de la Clinica</h6>
            </div>
            <div className="card-body">
              {/* Nombre Clinica */}
              <div className="mb-3">
                <label className="form-label" style={{ fontSize: "14px", fontWeight: 500, color: COLORS.textDark }}>
                  Nombre de clinica/consultorio *
                </label>
                <input
                  type="text"
                  className={`form-control ${errores.nombreClinica ? 'is-invalid' : ''}`}
                  name="nombreClinica"
                  value={formData.nombreClinica}
                  onChange={handleChange}
                  placeholder="Ej: Clinica Veterinaria San Marcos"
                  style={{ fontSize: "14px" }}
                />
                {errores.nombreClinica && (
                  <div className="invalid-feedback">{errores.nombreClinica}</div>
                )}
              </div>

              {/* Direccion */}
              <div className="mb-3">
                <label className="form-label" style={{ fontSize: "14px", fontWeight: 500, color: COLORS.textDark }}>
                  Direccion (opcional)
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="direccion"
                  value={formData.direccion}
                  onChange={handleChange}
                  placeholder="Ej: Av. Principal #123, Col. Centro"
                  style={{ fontSize: "14px" }}
                />
              </div>

              {/* Ciudad */}
              <div className="mb-3">
                <label className="form-label" style={{ fontSize: "14px", fontWeight: 500, color: COLORS.textDark }}>
                  Ciudad (opcional)
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="ciudad"
                  value={formData.ciudad}
                  onChange={handleChange}
                  placeholder="Ej: Veracruz, Ver."
                  style={{ fontSize: "14px" }}
                />
              </div>

              {/* Telefono */}
              <div className="mb-3">
                <label className="form-label" style={{ fontSize: "14px", fontWeight: 500, color: COLORS.textDark }}>
                  Telefono (opcional)
                </label>
                <input
                  type="tel"
                  className="form-control"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  placeholder="Ej: 555-123-4567"
                  style={{ fontSize: "14px" }}
                />
              </div>

              {/* Email - Solo lectura */}
              <div className="mb-0">
                <label className="form-label" style={{ fontSize: "14px", fontWeight: 500, color: COLORS.textDark }}>
                  Email de acceso
                </label>
                <div style={{
                  backgroundColor: COLORS.background,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: "6px",
                  padding: "10px 12px",
                  fontSize: "14px",
                  color: COLORS.textDark
                }}>
                  {formData.email || "No disponible"}
                </div>
                <small style={{ color: COLORS.textMuted, display: "block", marginTop: "4px" }}>
                  Este email es tu cuenta de acceso y no se puede cambiar
                </small>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="d-grid gap-2">
            <button
              type="submit"
              className="btn"
              disabled={saving}
              style={{
                backgroundColor: COLORS.primary,
                color: "white",
                fontWeight: 600,
                padding: "12px",
                fontSize: "16px"
              }}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Guardando...
                </>
              ) : (
                "Guardar perfil"
              )}
            </button>
          </div>

          <p style={{
            color: COLORS.textMuted,
            fontSize: "13px",
            textAlign: "center",
            marginTop: "12px"
          }}>
            * Campos obligatorios
          </p>
        </form>
      </div>
    </div>
  );
}
